// File: js/money/money-fold.js
//
// The cash projection. Imports nothing but its sibling dedupe helper (spec §8).
//
// Money exists only as events (invariant 1); every screen folds these same events, so
// no two screens can disagree. Audit H2 is exactly that disagreement: `getDashboardStats`
// counts voided transactions and ignores discounts while `reporting.js` subtracts both,
// so voiding a 25,000 receipt makes the answer depend on which tile you read.
//
// All arithmetic is integer FCFA (invariant 5). XAF has no subunit; there is no
// parseFloat and no toFixed anywhere in this file.

import { dedupeById } from './dedupe.js';

/** Kinds that move cash. Spec §3.1: sale `+` · void `−` · discount `−`. */
const CASH_KINDS = new Set(['sale', 'void', 'discount']);

/**
 * Kinds that carry `amount` as a bookkeeping value only. They move stock, not the
 * drawer, and are excluded from every cash total — but surfaced, never dropped.
 */
const STOCK_ONLY_KINDS = new Set(['restock', 'stock_adjustment']);

const CHANNELS = new Set(['regie', 'pharmacy']);

/**
 * A sale whose `channel` is absent or unrecognised still took real cash, so it is
 * counted — under this bucket rather than nowhere. Grouping must never change a total.
 */
const UNKNOWN_CHANNEL = 'unknown';

/**
 * @typedef {Object} MoneyEvent
 * @property {string} id Client-minted UUID (invariant 2).
 * @property {string} kind `'sale'|'void'|'discount'|'restock'|'stock_adjustment'`.
 * @property {string} [channel] `'regie'|'pharmacy'`; required on sales only.
 * @property {number} amount Positive integer FCFA.
 * @property {string} [reverses_id] Sale targeted by a void/discount.
 */

/**
 * @typedef {Object} ChannelTotals
 * @property {number} gross
 * @property {number} voided
 * @property {number} discounted
 * @property {number} net
 * @property {number} saleCount
 */

/**
 * @typedef {Object} IgnoredEvent
 * @property {string|null} id
 * @property {string|null} kind
 * @property {number|null} amount
 * @property {string} reason kebab-case code (invariant 7).
 */

/**
 * @typedef {Object} MoneyFold
 * @property {number} gross Sum of sales.
 * @property {number} voided Sum of voids.
 * @property {number} discounted Sum of discounts.
 * @property {number} net `gross − voided − discounted`.
 * @property {Object<string, ChannelTotals>} byChannel
 * @property {IgnoredEvent[]} ignored Events excluded from ALL totals, with a reason.
 */

/**
 * @param {Object<string, ChannelTotals>} byChannel
 * @param {string} channel
 * @returns {ChannelTotals}
 */
function bucketFor(byChannel, channel) {
    if (!byChannel[channel]) {
        byChannel[channel] = { gross: 0, voided: 0, discounted: 0, net: 0, saleCount: 0 };
    }
    return byChannel[channel];
}

/**
 * @param {*} event
 * @param {string} reason
 * @returns {IgnoredEvent}
 */
function ignoredEntry(event, reason) {
    const source = event && typeof event === 'object' ? event : {};
    return {
        id: source.id != null ? source.id : null,
        kind: typeof source.kind === 'string' ? source.kind : null,
        amount: Number.isInteger(source.amount) ? source.amount : null,
        reason
    };
}

/**
 * Folds ledger events into the cash projection.
 *
 * Never throws for a data-level problem (invariant 7): a malformed, unknown-kind or
 * non-cash event is excluded from **every** total and listed in `ignored[]` with a
 * reason. Half-counting is forbidden — an event that lands in `gross` also lands in a
 * `byChannel` bucket, so `sum(byChannel[*].gross) === gross` always holds, and an event
 * in `ignored[]` appears in no total at all.
 *
 * @param {MoneyEvent[]} events Ledger rows, any order. Deduped by `id` first.
 * @returns {MoneyFold}
 * @throws {TypeError} If `events` is not an array (programmer error).
 */
export function foldMoney(events) {
    const unique = dedupeById(events);

    let gross = 0;
    let voided = 0;
    let discounted = 0;
    /** @type {Object<string, ChannelTotals>} */
    const byChannel = {};
    /** @type {IgnoredEvent[]} */
    const ignored = [];

    // Pass 1: learn each sale's channel. A void carries no channel of its own (spec §8:
    // "Never filter server-side on a dimension the cancelling event does not carry" — a
    // server-side channel filter would drop voids and a voided sale would re-count).
    // Membership and grouping are fold-side only, which is what makes this lookup legal.
    const channelOfSale = new Map();
    for (const event of unique) {
        if (event && typeof event === 'object' && event.kind === 'sale' && event.id != null) {
            channelOfSale.set(event.id, CHANNELS.has(event.channel) ? event.channel : UNKNOWN_CHANNEL);
        }
    }

    for (const event of unique) {
        if (!event || typeof event !== 'object') {
            ignored.push(ignoredEntry(event, 'malformed-event'));
            continue;
        }
        if (event.id == null) {
            // Without an identity the row cannot be deduped, so counting it risks
            // double-counting a replayed push. Surface it instead.
            ignored.push(ignoredEntry(event, 'missing-id'));
            continue;
        }
        if (typeof event.kind !== 'string' || (!CASH_KINDS.has(event.kind) && !STOCK_ONLY_KINDS.has(event.kind))) {
            ignored.push(ignoredEntry(event, 'unknown-kind'));
            continue;
        }
        if (STOCK_ONLY_KINDS.has(event.kind)) {
            ignored.push(ignoredEntry(event, 'non-cash-kind'));
            continue;
        }
        // Mirrors the server-side numeric gate (spec §6): a positive integer or nothing.
        // No float ever enters a total — `12.5` is a bug, not half a franc.
        if (!Number.isInteger(event.amount) || event.amount <= 0) {
            ignored.push(ignoredEntry(event, 'amount-must-be-positive-integer'));
            continue;
        }

        if (event.kind === 'sale') {
            const channel = CHANNELS.has(event.channel) ? event.channel : UNKNOWN_CHANNEL;
            const bucket = bucketFor(byChannel, channel);
            gross += event.amount;
            bucket.gross += event.amount;
            bucket.saleCount += 1;
            continue;
        }

        // void / discount: attributed to the channel of the sale they cancel, falling
        // back to their own channel column, then to `unknown`. The reversed sale may be
        // outside this event set (a void pulled without its sale), and that must not
        // silently move money between channels.
        const channel = channelOfSale.get(event.reverses_id)
            || (CHANNELS.has(event.channel) ? event.channel : UNKNOWN_CHANNEL);
        const bucket = bucketFor(byChannel, channel);
        if (event.kind === 'void') {
            voided += event.amount;
            bucket.voided += event.amount;
        } else {
            discounted += event.amount;
            bucket.discounted += event.amount;
        }
    }

    for (const bucket of Object.values(byChannel)) {
        bucket.net = bucket.gross - bucket.voided - bucket.discounted;
    }

    return {
        gross,
        voided,
        discounted,
        net: gross - voided - discounted,
        byChannel,
        ignored
    };
}
